import { useCallback, useRef, useEffect, useState } from 'react'
import { logError } from '../lib/errorLogger.js'

// Helper for V2 API call
async function searchApiCallV2({ type, top_lat, left_lon, bottom_lat, right_lon, start_time, end_time, pagination_token }) {
    const params = new URLSearchParams()

    if (pagination_token) {
        // When using pagination token, only pass the token
        params.set('pagination_token', pagination_token)
    } else {
        // Initial search with all parameters
        params.set('version', 'v2')
        params.set('type', type)
        params.set('top_lat', String(top_lat))
        params.set('left_lon', String(left_lon))
        params.set('bottom_lat', String(bottom_lat))
        params.set('right_lon', String(right_lon))
        params.set('start_time', String(start_time))
        params.set('end_time', String(end_time))
    }

    const response = await fetch(`/api/search?${params.toString()}`)
    const text = await response.text()
    if (!response.ok) {
        throw new Error(text || 'Search request failed.')
    }
    if (!text) return { results: [], next_page_token: null }
    try {
        const parsed = JSON.parse(text)
        // Handle both old array format and new paginated format
        if (Array.isArray(parsed)) {
            return { results: parsed, next_page_token: null }
        }
        return {
            results: Array.isArray(parsed.results) ? parsed.results : [],
            next_page_token: parsed.next_page_token || null
        }
    } catch {
        return { results: [], next_page_token: null }
    }
}

// Helper for V1 API call
async function searchApiCallV1({ type, mapCenter, depthBase, startTime, endTime, pagination_token }) {
    const params = new URLSearchParams()

    if (pagination_token) {
        // When using pagination token, only pass the token
        params.set('pagination_token', pagination_token)
    } else {
        // Initial search with all parameters
        params.set('type', type)
        params.set('lon', String(mapCenter.lng))
        params.set('lat', String(mapCenter.lat))
        params.set('depth', String(type === 'events' ? depthBase + 1 : depthBase + 3))
        params.set('start_time', String(startTime))
        params.set('end_time', String(endTime))
    }

    const response = await fetch(`/api/search?${params.toString()}`)
    const text = await response.text()
    if (!response.ok) {
        throw new Error(text || 'Search request failed.')
    }
    if (!text) return { results: [], next_page_token: null }
    try {
        const parsed = JSON.parse(text)
        // Handle both old array format and new paginated format
        if (Array.isArray(parsed)) {
            return { results: parsed, next_page_token: null }
        }
        return {
            results: Array.isArray(parsed.results) ? parsed.results : [],
            next_page_token: parsed.next_page_token || null
        }
    } catch {
        return { results: [], next_page_token: null }
    }
}

export function useSearch({
    mapReady,
    mapCenter,
    mapZoom,
    mapBounds,
    selectedDate,
    setResults,
    setSelectedId,
    setLoading,
    setError,
}) {
    // Pagination state - stores next page tokens for events and places
    const [nextPageTokens, setNextPageTokens] = useState({ events: null, places: null })
    const [hasMore, setHasMore] = useState(false)
    const [loadingMore, setLoadingMore] = useState(false)

    // Store search parameters for pagination
    const lastSearchParams = useRef(null)

    const handleSearch = useCallback(async () => {
        if (!mapReady) {
            return
        }

        const parsedDate = new Date(`${selectedDate}T00:00:00`)
        if (Number.isNaN(parsedDate.getTime())) {
            setError('Please choose a valid date.')
            return
        }

        setError('')
        setLoading(true)

        try {
            let events = []
            let places = []
            let eventsToken = null
            let placesToken = null

            // If mapBounds is provided and valid, use V2 API
            if (
                mapBounds &&
                typeof mapBounds.getNorthEast === 'function' &&
                typeof mapBounds.getSouthWest === 'function'
            ) {
                const ne = mapBounds.getNorthEast()
                const sw = mapBounds.getSouthWest()
                const top_lat = ne.lat()
                const left_lon = sw.lng()
                const bottom_lat = sw.lat()
                const right_lon = ne.lng()

                const startTime = parsedDate.getTime()
                const endDate = new Date(startTime)
                endDate.setDate(endDate.getDate() + 1)
                const endTime = endDate.getTime()

                // Store search params for pagination
                lastSearchParams.current = {
                    useV2: true,
                    top_lat, left_lon, bottom_lat, right_lon, startTime, endTime
                }

                const [eventsPayloadV2, placesPayloadV2] = await Promise.all([
                    searchApiCallV2({
                        type: 'events',
                        top_lat, left_lon, bottom_lat, right_lon, start_time: startTime, end_time: endTime,
                    }),
                    searchApiCallV2({
                        type: 'places',
                        top_lat, left_lon, bottom_lat, right_lon, start_time: startTime, end_time: endTime,
                    }),
                ])

                events = eventsPayloadV2.results.map(item => ({ ...item, searchType: 'events' }))
                places = placesPayloadV2.results.map(item => ({ ...item, searchType: 'places' }))
                eventsToken = eventsPayloadV2.next_page_token
                placesToken = placesPayloadV2.next_page_token
            } else {
                // Use V1 API if mapBounds is not provided
                const startTime = parsedDate.getTime()
                const endDate = new Date(startTime)
                endDate.setDate(endDate.getDate() + 1)
                const endTime = endDate.getTime()
                const depth = Math.round(mapZoom) + 1

                // Store search params for pagination
                lastSearchParams.current = {
                    useV2: false,
                    mapCenter, depth, startTime, endTime
                }

                const [eventsPayloadV1, placesPayloadV1] = await Promise.all([
                    searchApiCallV1({
                        type: 'events',
                        mapCenter, depthBase: depth, startTime, endTime,
                    }),
                    searchApiCallV1({
                        type: 'places',
                         mapCenter, depthBase: depth, startTime, endTime,
                    }),
                ])

                events = eventsPayloadV1.results.map(item => ({ ...item, searchType: 'events' }))
                places = placesPayloadV1.results.map(item => ({ ...item, searchType: 'places' }))
                eventsToken = eventsPayloadV1.next_page_token
                placesToken = placesPayloadV1.next_page_token
            }

            const combined = [...events, ...places]
            setResults(combined)
            setSelectedId(prev => (combined.some(item => item.id === prev) ? prev : null))

            // Update pagination state
            setNextPageTokens({ events: eventsToken, places: placesToken })
            setHasMore(eventsToken !== null || placesToken !== null)
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Unknown error'
            logError('SEARCH_ERROR', 'Search API call failed', {
                message: errorMessage,
                stack: err instanceof Error ? err.stack : undefined,
                selectedDate,
                mapCenter,
                mapZoom,
                hasBounds: !!mapBounds,
            })
            setError(errorMessage)
        } finally {
            setLoading(false)
        }
    }, [mapReady, mapBounds, mapCenter, mapZoom, selectedDate, setResults, setSelectedId, setLoading, setError])

    const loadMore = useCallback(async () => {
        if (!hasMore || loadingMore) {
            return
        }

        setLoadingMore(true)
        setError('')

        try {
            let newEvents = []
            let newPlaces = []
            let newEventsToken = null
            let newPlacesToken = null

            // Fetch next page for events if token exists
            if (nextPageTokens.events) {
                const eventsPayload = lastSearchParams.current?.useV2
                    ? await searchApiCallV2({ pagination_token: nextPageTokens.events })
                    : await searchApiCallV1({ pagination_token: nextPageTokens.events })

                newEvents = eventsPayload.results.map(item => ({ ...item, searchType: 'events' }))
                newEventsToken = eventsPayload.next_page_token
            }

            // Fetch next page for places if token exists
            if (nextPageTokens.places) {
                const placesPayload = lastSearchParams.current?.useV2
                    ? await searchApiCallV2({ pagination_token: nextPageTokens.places })
                    : await searchApiCallV1({ pagination_token: nextPageTokens.places })

                newPlaces = placesPayload.results.map(item => ({ ...item, searchType: 'places' }))
                newPlacesToken = placesPayload.next_page_token
            }

            // Append new results to existing ones
            setResults(prev => [...prev, ...newEvents, ...newPlaces])

            // Update pagination state
            // If we fetched a page, use the new token (which may be null if no more pages)
            // If we didn't fetch (no token existed), keep the old token
            setNextPageTokens({
                events: nextPageTokens.events ? newEventsToken : nextPageTokens.events,
                places: nextPageTokens.places ? newPlacesToken : nextPageTokens.places
            })

            // Check if either new token exists, OR if we still have unfetched tokens
            const eventsHasMore = nextPageTokens.events ? newEventsToken !== null : false
            const placesHasMore = nextPageTokens.places ? newPlacesToken !== null : false
            setHasMore(eventsHasMore || placesHasMore)
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Unknown error'
            logError('LOAD_MORE_ERROR', 'Load more API call failed', {
                message: errorMessage,
                stack: err instanceof Error ? err.stack : undefined,
            })
            setError(errorMessage)
        } finally {
            setLoadingMore(false)
        }
    }, [hasMore, loadingMore, nextPageTokens, setResults, setError])

    const handleSearchRef = useRef(handleSearch)
    useEffect(() => {
        handleSearchRef.current = handleSearch
    }, [handleSearch])

    return { handleSearch, handleSearchRef, loadMore, hasMore, loadingMore }
}
